import React from "react"
import {
	Flex,
	Text,
	Image,
	Button,
	Rte,
	Icon,
	scale,
	colors,
	breakpoint,
	INNER_MAX_WIDTH,
	READABLE_LINE_MAX_WIDTH,
	TagStyled,
	VideoPlayer,
} from "@capioweb/ui-components"
import styled from "styled-components"
import {
	ContentAreaProperty,
	XhtmlString,
	QBankImage,
	PropertyCategory,
} from "../../@types/CapioWeb/core"
import {
	StringProperty,
	LinkProperty,
} from "../../@types/EpiFoundationSpa/Property"
import {
	PageBaseProps,
	BreadcrumbItemProps,
} from "../../components/Core/pageBase"
import { BlockResolver, BreadcrumbsBlock } from "../../components"
import { EpiContext } from "../../context"
import { LocalesContext } from "../../context/LocalesContext"
import { onFavButtonClick } from "../../components/MyPagesPersonalizedArticlesBlock/api"
import {
	shouldRender,
	imageBlockDataToImageProps,
	createMarkup,
	assetBaseUrl,
} from "../../utils"
import { sendArticleDataLayerPush } from "../../utils/piwikPro"
import { useLocation } from "react-router-dom"
import { scrollToAnchor } from "../../utils/scrollToAnchor"

interface LockedContentProps {
	heading: string
	description: string
	link: LinkProperty
}

interface ContentHubArticlePageProps extends PageBaseProps {
	heading: StringProperty
	preamble?: StringProperty
	body?: XhtmlString
	imageDesktopBlock?: QBankImage
	youtubeUrl: StringProperty
	mainContentArea: ContentAreaProperty
	cta?: ContentAreaProperty
	categories: PropertyCategory
	breadcrumb: Array<BreadcrumbItemProps>
	isLockedContent?: boolean
	lockedContent?: LockedContentProps
	isLoggedIn?: boolean
	isSavedArticle?: boolean
}

const RoundedBox = styled(Flex)`
	position: relative;
	border-radius: ${scale.px(3)};

	&:before {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: ${scale.px(10)};
		transform: translateY(-100%);
		background: linear-gradient(
			180deg,
			rgba(255, 255, 255, 0.32) 0%,
			${colors.white} 100%
		);
	}

	${breakpoint("m")`
		&:before {
			height: ${scale.px(17)};
		}
	`}
`

const LockedContentBlock: React.FC<LockedContentProps> = (props) => {
	return (
		<RoundedBox
			flexDirection="column"
			backgroundColor="blue11"
			p={{ _: 3, m: 5 }}
			mb={{ _: 3, m: 5 }}
		>
			<Text as="h3" variant="headline-3" mb={1.5}>
				{props.heading}
			</Text>
			<Text mb={4}>{props.description}</Text>
			<Flex>
				<Button.Link to={props.link?.href ?? "#"} target={props.link.target}>
					{props.link.text}
				</Button.Link>
			</Flex>
		</RoundedBox>
	)
}

const SaveArticleButton = styled.button`
	display: flex;
	align-items: center;
	text-decoration: underline;
	font-size: ${scale.px(1.75)};
	line-height: ${scale.px(2.5)};
`

const ContentHubArticlePage: React.FC<ContentHubArticlePageProps> = (props) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const { editMode } = React.useContext(EpiContext)
	const hasImage = props.imageDesktopBlock?.media?.value?.url ? true : false
	const hasVideo = props.youtubeUrl?.value ? true : false
	const srcSetSizes = [
		{ width: 1000, breakpoint: 768 },
		{ width: 1300, breakpoint: 992 },
		{ width: 1600, breakpoint: 2560 },
	]
	const [isSavedArticle, setIsSavedArticle] = React.useState(
		props.isSavedArticle ?? false
	)

	const { hash } = useLocation()

	const id = decodeURI(hash.substring(1))

	const el = document?.getElementById(id)

	React.useEffect(() => {
		if (el) {
			//Scroll to anchorID position if it exists
			scrollToAnchor(el)
		}
	}, [el]) //UseEffect will run again when 'el' is ready

	React.useEffect(() => {
		sendArticleDataLayerPush("viewed", props.heading.value)
	}, [])

	return (
		<Flex display="block" w={1}>
			<BreadcrumbsBlock items={...props.breadcrumb} />
			<Flex as="section" w={1} px={3}>
				<Flex
					w={1}
					flexDirection="column"
					maxw={INNER_MAX_WIDTH}
					style={{ margin: "0 auto" }}
				>
					<Flex
						w={{ _: "calc(100% + 48px)", m: 1 }}
						ml={{ _: -3, m: 0 }}
						mb={hasImage ? { _: 3, m: 4 } : 5}
					>
						{hasImage &&
							(hasVideo ? (
								<VideoPlayer
									locales={{
										playBtnLabel: translate(translationKeys.playVideo),
									}}
									posterUrl={
										assetBaseUrl() + props.imageDesktopBlock.media.value.url
									}
									autoplay={false}
									url={props.youtubeUrl.value}
								/>
							) : (
								<Image
									data-epi-edit="imageDesktopBlock"
									{...imageBlockDataToImageProps(
										props.imageDesktopBlock,
										true,
										true,
										srcSetSizes
									)}
									ratio={{ _: 420 / 721, l: 420 / 1128 }}
								/>
							))}
					</Flex>
					<Flex
						w={1}
						flexDirection={{ _: "column", l: "row" }}
						columnGap={{ l: 3 }}
					>
						<Flex
							w={1}
							flex={{ _: 1, l: 8 / 12 }}
							flexDirection="column"
							mb={2}
							maxw={READABLE_LINE_MAX_WIDTH}
						>
							<Flex w={1} flexDirection={"row"} columnGap={{ l: 3 }} mb={2}>
								{props.categories?.value.length > 0 && (
									<TagStyled>{props.categories.value[0].description}</TagStyled>
								)}
								{/* {props.isLoggedIn && (
									<SaveArticleButton
										onClick={(e) => {
											e.preventDefault()
											onFavButtonClick(
												props.contentLink.guidValue,
												props.isSavedArticle,
												setIsSavedArticle
											)
											sendArticleDataLayerPush("saved", props.heading.value)
										}}
									>
										<Icon
											color="navy"
											icon={isSavedArticle ? "heart-filled" : "heart"}
											mr={0.25}
										/>
										{`${
											isSavedArticle ? translate(translationKeys.saved : translate(translationKeys.save
										} ${translate(translationKeys.article.toLowerCase()}`}
									</SaveArticleButton>
								)} */}
							</Flex>
							{props.heading?.value && (
								<Text variant="headline-1">{props.heading.value}</Text>
							)}

							{props.preamble?.value && (
								<Text variant="preamble">{props.preamble.value}</Text>
							)}

							{props.cta?.value && (
								<Flex
									w="auto"
									mb={3}
									display={{ _: "flex", l: "none" }}
									flexDirection="column"
									rowGap={2}
								>
									<BlockResolver blocks={props.cta?.value} />
								</Flex>
							)}

							{props.body?.value && (
								<Rte>
									<div
										dangerouslySetInnerHTML={createMarkup(props.body.value)}
									/>
								</Rte>
							)}
							{props.isLockedContent && props.lockedContent && (
								<LockedContentBlock {...props.lockedContent} />
							)}
						</Flex>
						{props.cta?.value && (
							<Flex
								w={1}
								mb={3}
								flex={{ _: 1, l: 4 / 12 }}
								alignSelf="flex-start"
								display={{ _: "none", l: "flex" }}
								flexDirection="column"
								rowGap={3}
							>
								<BlockResolver blocks={props.cta?.value} />
							</Flex>
						)}
					</Flex>
				</Flex>
			</Flex>
			{shouldRender(editMode.inEditMode, props.mainContentArea?.value) && (
				<div data-epi-edit={"mainContentArea"}>
					<BlockResolver blocks={props.mainContentArea?.value} />
				</div>
			)}
		</Flex>
	)
}

export { ContentHubArticlePage }
